import { WIDGET_TYPE } from 'business/constants';
import { PageSeparator } from 'business/definitions';
import { getRowHeight } from 'helpers/layoutHelpers';
import _ from 'lodash';
import { Layout, Layouts } from 'react-grid-layout';
import {
    Breakstyle,
    WidgetT,
} from 'routes/Dashboard/components/WidgetsLayout/helpers';
import { getPageIndex } from 'routes/Dashboard/components/WidgetsLayout/PrintableGrid/helpers/paginationHelpers';

const getPageBreakWidgetHeight = (
    widgetYPos: number,
    pageSeparators: PageSeparator[],
    isFullWidth: boolean,
): number => {
    const widgetPageIndex = getPageIndex(widgetYPos);
    if (widgetPageIndex + 1 > pageSeparators.length) {
        return 1;
    }
    const pageSeparator = _.sortBy(pageSeparators, (sep) => sep.yPos)[
        widgetPageIndex
    ];
    return pageSeparator.yPos / getRowHeight(isFullWidth) - widgetYPos; // in RGL position
};

export const getPositionConstraintsFromType = (
    widget: WidgetT,
    pageSeparators: PageSeparator[],
    isFullWidth: boolean,
): Partial<Layout> => {
    switch (widget.type) {
        case WIDGET_TYPE.separator:
            return { minW: 10, minH: 2, w: 10, h: 2, isResizable: false };
        case WIDGET_TYPE.title:
            return { minW: 10, minH: 2, w: 10, h: 2, isResizable: false };
        case WIDGET_TYPE.navlink:
            return { minW: 2, minH: 2 };
        case WIDGET_TYPE.tableofcontent:
            return { minW: 2, minH: 2 };
        case WIDGET_TYPE.paragraph:
            return { minH: 2 };
        case WIDGET_TYPE.pagebreak: {
            const widgetHeight = getPageBreakWidgetHeight(
                widget.position.md.y!,
                pageSeparators,
                isFullWidth,
            );
            return {
                minW: 10,
                w: 10,
                h: widgetHeight,
                isResizable: false,
            };
        }
        default:
            return { minH: 4, minW: 1 };
    }
};

/*
 * The current breakpoint is the one for which we have elements - RGL returns the empty list
 * for other breakpoints.
 */
const findBreakPointFromLayout = (layout: Layouts): Breakstyle =>
    layout.xs && layout.xs.length > 0 ? 'xs' : 'md';

/**
 * Return an unique key composed of the widget key and its position.
 * We need the position so when we resize or move a widget, we can rerender all the impacted ones
 */
export const getWidgetKey = (
    widget: WidgetT,
    breakpoint: Breakstyle,
): string => {
    const { x, y, w, h } = widget.position[breakpoint];
    return `${widget.key}-${x}-${y}-${w}-${h}`;
};

// TODO: unit test
/**
    The React grid component deals with positions: RGLayoutItemT[]
    The widget states have additional info (like type, chart, etc)
    When widgets's position is edited on editMode, we need to update the
    widget with new position.
*/
export const buildWidgetsFromRGLPositions = (
    layout: Layouts,
    currentWidgets: WidgetT[],
    pageSeparators: PageSeparator[],
    isFullWidth: boolean,
): WidgetT[] => {
    // NOTE - we would like to use the breakpoint from RGLLayoutEngine's state instead of
    // manually computing it here, but unfortunately RGL's onLayoutChange
    // is called before `onbreakPointChange`, which implies that breakpoint from
    // RGLLayoutEngine state is potentially not up to to date.
    const breakpoint = findBreakPointFromLayout(layout);
    const actualLayout = layout[breakpoint];
    return currentWidgets.map((w) => {
        const currentLayout = actualLayout.find(
            (l) => l.i === getWidgetKey(w, breakpoint),
        );
        if (!currentLayout) {
            // This used to be a throw, but it happened very often without any consequences
            // for the user.
            console.warn(
                `buildWidgetsFromRGLPositions error: key ${w.key} was not found`,
            );
            return w;
        }
        const widgetHeight =
            w.type === WIDGET_TYPE.pagebreak
                ? getPageBreakWidgetHeight(
                      currentLayout.y,
                      pageSeparators,
                      isFullWidth,
                  )
                : currentLayout.h;
        return {
            ...w,
            position: {
                ...w.position,

                [breakpoint]: {
                    x: currentLayout.x,
                    y: currentLayout.y,
                    h: widgetHeight,
                    w: currentLayout.w,
                },
            },
        };
    });
};

const isOverlapping = (widget: WidgetT, position: WidgetT['position']) =>
    widget.position.md.y <= position.md.y + position.md.h - 1 &&
    widget.position.md.y + widget.position.md.h > position.md.y + position.md.h;

const isDeeper = (current: WidgetT, previousPos: WidgetT['position']) =>
    current.position.md.y + current.position.md.h >
    previousPos.md.y + previousPos.md.h;

// compute insertion distance for widget according to other overlapping widgets
export const getOverlappingDistance = (
    position: WidgetT['position'],
    widgets: WidgetT[],
): number => {
    const overlappingWidgets = widgets.filter((item) =>
        isOverlapping(item, position),
    );
    if (overlappingWidgets.length === 0) return position.md.h;
    else {
        const deepest = overlappingWidgets.reduce(
            (deep, curr) => {
                if (isDeeper(curr, deep.position)) return curr;
                return deep;
            },
            { position: position },
        );
        return (
            deepest.position.md.y -
            position.md.y +
            getOverlappingDistance(deepest.position, widgets)
        );
    }
};

//TODO: find max distance between deleted widget and other vertical overlapping widgets
export const moveWidgetDown = (
    widget: WidgetT,
    insertedWidget: WidgetT,
    fromPosition: WidgetT['position'],
    distance: number,
) => {
    // Only adjust items that are below the target element
    if (widget.position.md.y === undefined || fromPosition.md.y === undefined)
        return widget;
    if (widget.position.md.y >= fromPosition.md.y + distance) {
        widget.position.md.y += insertedWidget.position.md.h;
    }
    if (widget.position.xs.y === undefined || fromPosition.xs.y === undefined)
        return widget;
    if (widget.position.xs.y >= fromPosition.xs.y + distance) {
        widget.position.xs.y += insertedWidget.position.xs.h;
    }
    return widget;
};

//TODO: find distance between deleted widget and other vertical overlapping widgets
export const moveWidgetUp = (
    widget: WidgetT,
    deletedWidget: WidgetT,
    nextWidgetInRange?: WidgetT,
) => {
    // Only adjust items that are below the target element
    let OFFSET_MD_Y = deletedWidget.position.md.h;
    let OFFSET_SX_Y = deletedWidget.position.xs.h;
    if (nextWidgetInRange !== undefined) {
        OFFSET_MD_Y =
            nextWidgetInRange.position.md.y - deletedWidget.position.md.y;
        OFFSET_SX_Y =
            nextWidgetInRange.position.xs.y - deletedWidget.position.xs.y;
    }

    if (
        widget.position.md.y === undefined ||
        deletedWidget.position.md.y === undefined
    )
        return widget;
    if (widget.position.md.y > deletedWidget.position.md.y) {
        widget.position.md.y -= OFFSET_MD_Y;
    }
    if (
        widget.position.xs.y === undefined ||
        deletedWidget.position.xs.y === undefined
    )
        return widget;
    if (widget.position.xs.y > deletedWidget.position.xs.y) {
        widget.position.xs.y -= OFFSET_SX_Y;
    }
    return widget;
};

export const isWidgetInRange = (widget: WidgetT, deletedWidget: WidgetT) =>
    widget.position.md.y >= deletedWidget.position.md.y &&
    widget.position.md.y <
        deletedWidget.position.md.y + deletedWidget.position.md.h;

export const findNextWidgetInRange = (widgets: WidgetT[], deleted: WidgetT) =>
    widgets.reduce((agg: WidgetT, curr) => {
        if (isWidgetInRange(curr, deleted)) {
            if (agg?.position.md.y < curr.position.md.y) return agg;
            else {
                return curr;
            }
        }
        return agg;
    }, undefined);

export const patchWidgetsPositionOnInsert = (
    widgets: WidgetT[],
    data: WidgetT,
    position?: WidgetT['position'],
): WidgetT[] => {
    if (position === undefined) return widgets;
    else {
        const insertionDistance = getOverlappingDistance(position, widgets);
        return widgets.map((item: WidgetT) =>
            moveWidgetDown(item, data, position, insertionDistance),
        );
    }
};

export const patchWidgetsPositionOnDelete = (
    widgets: WidgetT[],
    deleted: WidgetT,
): WidgetT[] => {
    const nextWidgetInRange = findNextWidgetInRange(widgets, deleted);
    return widgets.map((item: WidgetT) =>
        moveWidgetUp(item, deleted, nextWidgetInRange),
    );
};

const positionMatchForWidgets = (
    widgetA: WidgetT,
    widgetB: WidgetT,
    breakdown: Breakstyle,
): boolean =>
    widgetA.position[breakdown].y === widgetB.position[breakdown].y &&
    widgetA.position[breakdown].x === widgetB.position[breakdown].x &&
    widgetA.position[breakdown].h === widgetB.position[breakdown].h &&
    widgetA.position[breakdown].w === widgetB.position[breakdown].w;

export const widgetChanged = (
    currentWidgets: WidgetT[],
    newWidgets: WidgetT[],
    breakdown: Breakstyle,
): boolean => {
    if (currentWidgets.length !== newWidgets.length) return true;
    return !currentWidgets.every((currentItem) =>
        newWidgets.some((newItem) =>
            positionMatchForWidgets(newItem, currentItem, breakdown),
        ),
    );
};
